import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  DocumentDto,
  DocumentLogEntry,
  DocumentProgressUpdate,
  DocumentStatus,
  DocumentStatusChangedEvent,
  DocumentEstimate,
  DocumentVersionSummaryDto,
} from '../types/document';
import { isTerminalDocumentStatus } from '../types/document';
import { toCamelCase } from '../lib/apiClient';
import { usePluginStore } from '@/stores/usePluginStore';
import { useTriageStore } from '@/stores/useTriageStore';
import { useQueryStore } from '@/app/features/talk-to-data/useQueryStore';
import { logger } from '@/lib/logger';

/** Processing details populated by SignalR, attached to a document */
export interface DocumentProcessingDetails {
  stage?: string;
  current?: number;
  total?: number;
  logs: DocumentLogEntry[];
  lastUpdate: string;
  /** Accumulates every stage name seen via ProgressUpdate — never shrinks. */
  seenStages?: string[];
}

interface DocumentState {
  documents: DocumentDto[];
  isUploading: boolean;
  error: string | null;
  /** Per-document processing details from SignalR (documentId → details) */
  processingDetails: Record<string, DocumentProcessingDetails>;
  /** Version history cache (documentId → versions) */
  versionHistory: Record<string, DocumentVersionSummaryDto[]>;
  /** Whether to show all document versions or only current */
  showAllVersions: boolean;
  /** Document IDs pending triage (collected via debounce) */
  triagePending: string[];

  // Actions
  fetchDocuments: (workspaceId: string, token?: string | null) => Promise<void>;
  uploadDocument: (
    file: File,
    workspaceId: string,
    domainId: string,
    token?: string | null,
    versionLabel?: string | null,
  ) => Promise<DocumentDto | null>;
  deleteDocument: (id: string, token?: string | null) => Promise<void>;
  retryDocument: (id: string, token: string) => Promise<boolean>;
  forceCompleteDocument: (id: string, token: string) => Promise<boolean>;
  triggerProcess: (id: string, token?: string | null) => Promise<void>;
  fetchEstimate: (id: string, token?: string | null) => Promise<DocumentEstimate | null>;
  fetchVersionHistory: (documentId: string, token?: string | null) => Promise<DocumentVersionSummaryDto[]>;
  setShowAllVersions: (show: boolean) => void;
  addToTriageBatch: (documentId: string, workspaceId: string, token?: string | null) => void;

  // SignalR-driven updates (replaces polling)
  setDocumentStatus: (documentId: string, status: DocumentStatus, message?: string) => void;
  applyDocumentSnapshot: (data: DocumentStatusChangedEvent) => void;
  updateDocumentProgress: (data: DocumentProgressUpdate) => void;
  addDocumentLog: (data: DocumentLogEntry) => void;
  addDocumentError: (documentId: string, error: string) => void;
  getProcessingDetails: (documentId: string) => DocumentProcessingDetails | undefined;
  clearProcessingDetails: (documentId: string) => void;
  fetchProcessingLogs: (documentId: string, token?: string | null) => Promise<void>;
}

/**
 * useDocumentStore - Zustand store for document upload and management
 *
 * Manages document lifecycle, upload progress, and status polling.
 */

/** Module-level debounce timer for upload-triage batch collection */
let _triageDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const TRIAGE_DEBOUNCE_MS = 5000;

export const useDocumentStore = create<DocumentState>()(
  persist(
    (set, get) => ({
  documents: [],
  isUploading: false,
  error: null,
  processingDetails: {},
  versionHistory: {},
  showAllVersions: false,
  triagePending: [],

  /**
   * Fetch all documents for a workspace
   */
  fetchDocuments: async (workspaceId, token) => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const { showAllVersions } = get();
      const url = `/api/dotnet/documents?workspaceId=${workspaceId}${showAllVersions ? '&includeAllVersions=true' : ''}`;
      const response = await fetch(url, { headers });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized.');
        }
        throw new Error('Failed to load documents.');
      }

      const documents = (await response.json()) as DocumentDto[];
      set({
        documents: Array.isArray(documents) ? documents : [],
        error: null,
      });
    } catch (error) {
      set({
        documents: [],
        error: error instanceof Error ? error.message : 'Failed to load documents.',
      });
    }
  },

  /**
   * Upload a PDF document
   */
  uploadDocument: async (file, workspaceId, domainId, token, versionLabel) => {
    set({ isUploading: true, error: null });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('workspaceId', workspaceId);
      formData.append('domainId', domainId);
      if (versionLabel) {
        formData.append('versionLabel', versionLabel);
      }

      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/dotnet/documents/upload', {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Upload failed with status ${response.status}`
        );
      }

      const document = (await response.json()) as DocumentDto;

      // Add document to list
      set((state) => ({
        documents: [document, ...state.documents],
        isUploading: false,
        error: null,
      }));

      // Check if workspace has machine_aware enabled → batch for triage.
      // Workspace settings are loaded asynchronously after boot, so reload once
      // here if the ingest settings are not available yet.
      let ingestSettings = usePluginStore.getState()
        .workspaceSettings
        .find((s) => s.pluginId === 'ingest');

      if (!ingestSettings) {
        await usePluginStore.getState().fetchWorkspaceSettings(workspaceId, token);
        ingestSettings = usePluginStore.getState()
          .workspaceSettings
          .find((s) => s.pluginId === 'ingest');
      }

      const isMachineAware =
        ingestSettings?.enabled !== false &&
        ingestSettings?.settingsJson?.machine_aware === true;

      if (isMachineAware) {
        get().addToTriageBatch(document.id, workspaceId, token);
      } else {
        // Standard flow: immediately trigger processing pipeline
        get().triggerProcess(document.id, token).catch((error) => {
          logger.error('useDocumentStore', 'Failed to trigger document processing', error);
        });
      }

      return document;
    } catch (error) {
      set({
        isUploading: false,
        error:
          error instanceof Error ? error.message : 'Failed to upload document.',
      });
      return null;
    }
  },

  /**
   * Delete a document
   */
  deleteDocument: async (id, token) => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`/api/dotnet/documents/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to delete document.');
      }

      // Remove document from list
      set((state) => ({
        documents: state.documents.filter((doc) => doc.id !== id),
        error: null,
      }));

      // Clear stale machine context — the pinned machine may have been
      // linked to the deleted document.  Next query will re-trigger
      // disambiguation so the user can pick from the remaining machines.
      void useQueryStore.getState().clearMachineContext();
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : 'Failed to delete document.',
      });
      throw error;
    }
  },

  /**
   * Retry failed chunks for a PartiallyFailed document
   */
  retryDocument: async (id, token) => {
    // Optimistically set status to Processing so the UI reflects activity
    get().setDocumentStatus(id, 'Processing' as DocumentStatus);
    const response = await fetch(`/api/dotnet/documents/${id}/retry`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      // SignalR will deliver the real status — no manual revert needed
      return false;
    }
    return true;
  },

  /**
   * Force-mark a PartiallyFailed document as Completed
   */
  forceCompleteDocument: async (id, token) => {
    const response = await fetch(`/api/dotnet/documents/${id}/force-complete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return false;
    get().setDocumentStatus(id, 'Completed');
    return true;
  },

  /**
   * Trigger document processing (parsing)
   */
  triggerProcess: async (id, token) => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`/api/dotnet/documents/${id}/process`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || 'Failed to trigger document processing.'
        );
      }

      // Update document status to Parsing
      set((state) => ({
        documents: state.documents.map((doc) =>
          doc.id === id ? { ...doc, status: 'Parsing' as DocumentStatus } : doc
        ),
        error: null,
      }));
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Failed to trigger processing.',
      });
      throw error;
    }
  },

  /**
   * Fetch processing estimate from Python backend
   */
  fetchEstimate: async (id, token) => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`/api/dotnet/documents/${id}/estimate`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to get processing estimate.');
      }

      const estimate = toCamelCase<DocumentEstimate>(await response.json());
      return estimate;
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : 'Failed to get estimate.',
      });
      return null;
    }
  },

  // ─── SignalR-driven updates ─────────────────────────────────────────────────

  /**
   * Optimistically update a document's status before the authoritative SignalR
   * snapshot arrives.
   */
  setDocumentStatus: (documentId, status, message) => {
    set((state) => ({
      documents: state.documents.map((doc) =>
        doc.id === documentId
          ? { ...doc, status: status as DocumentStatus }
          : doc,
      ),
      processingDetails: {
        ...state.processingDetails,
        [documentId]: {
          ...(state.processingDetails[documentId] || { logs: [], lastUpdate: '' }),
          lastUpdate: new Date().toISOString(),
          ...(message
            ? {
                logs: [
                  ...(state.processingDetails[documentId]?.logs || []),
                  {
                    documentId,
                    level: 'info',
                    message,
                    timestamp: new Date().toISOString(),
                  },
                ],
              }
            : {}),
        },
      },
    }));
  },

  /**
   * Apply an authoritative document snapshot from SignalR.
   */
  applyDocumentSnapshot: (data) => {
    set((state) => {
      const existing = state.processingDetails[data.documentId] || {
        logs: [],
        lastUpdate: data.timestamp,
      };

      return {
        documents: state.documents.map((doc) =>
          doc.id === data.documentId
            ? {
                ...doc,
                status: data.status,
                processedChunks: data.processedChunks,
                totalChunks: data.totalChunks,
                failedChunks: data.failedChunks,
                completedAt: data.completedAt ?? doc.completedAt,
                errorMessage: data.errorMessage ?? doc.errorMessage,
              }
            : doc,
        ),
        processingDetails: {
          ...state.processingDetails,
          [data.documentId]: {
            ...existing,
            lastUpdate: data.timestamp,
            ...(isTerminalDocumentStatus(data.status)
              ? {
                  stage: undefined,
                  current: undefined,
                  total: undefined,
                }
              : {}),
            ...(data.message
              ? {
                  logs: [
                    ...existing.logs,
                    {
                      documentId: data.documentId,
                      level: 'info',
                      message: data.message,
                      timestamp: data.timestamp,
                    },
                  ],
                }
              : {}),
          },
        },
      };
    });
  },

  /**
   * Update progress from a SignalR ProgressUpdate event.
   */
  updateDocumentProgress: (data) => {
    set((state) => {
      const existing = state.processingDetails[data.documentId] || {
        logs: [],
        lastUpdate: data.timestamp,
      };

      const seenStages = existing.seenStages ?? [];
      const updatedSeenStages = seenStages.includes(data.stage)
        ? seenStages
        : [...seenStages, data.stage];

      return {
        processingDetails: {
          ...state.processingDetails,
          [data.documentId]: {
            ...existing,
            stage: data.stage,
            current: data.current,
            total: data.total,
            lastUpdate: data.timestamp,
            seenStages: updatedSeenStages,
            ...(data.message
              ? {
                  logs: [
                    ...existing.logs,
                    {
                      documentId: data.documentId,
                      level: 'info' as const,
                      message: data.message,
                      timestamp: data.timestamp,
                    },
                  ],
                }
              : {}),
          },
        },
      };
    });
  },

  /**
   * Add a log entry from a SignalR LogEntry event.
   * Deduplicates against entries already fetched from the server
   * using the same timestamp|message key as fetchProcessingLogs.
   */
  addDocumentLog: (data) => {
    set((state) => {
      const existing = state.processingDetails[data.documentId] || {
        logs: [],
        lastUpdate: data.timestamp,
      };

      // Skip if a log with the same timestamp+message already exists
      const key = `${data.timestamp}|${data.message}`;
      const isDuplicate = existing.logs.some(
        (l) => `${l.timestamp}|${l.message}` === key
      );
      if (isDuplicate) return state;

      return {
        processingDetails: {
          ...state.processingDetails,
          [data.documentId]: {
            ...existing,
            logs: [...existing.logs, data],
            lastUpdate: data.timestamp,
          },
        },
      };
    });
  },

  /**
   * Add an error from a SignalR Error event.
   */
  addDocumentError: (documentId, error) => {
    set((state) => {
      const existing = state.processingDetails[documentId] || {
        logs: [],
        lastUpdate: '',
      };
      const timestamp = new Date().toISOString();

      return {
        processingDetails: {
          ...state.processingDetails,
          [documentId]: {
            ...existing,
            logs: [
              ...existing.logs,
              { documentId, level: 'error', message: error, timestamp },
            ],
            lastUpdate: timestamp,
          },
        },
      };
    });
  },

  /**
   * Get processing details for a specific document.
   */
  getProcessingDetails: (documentId) => {
    return get().processingDetails[documentId];
  },

  /**
   * Fetch the version history for a document.
   */
  fetchVersionHistory: async (documentId, token) => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(
        `/api/dotnet/documents/${documentId}/versions`,
        { headers }
      );
      if (!response.ok) {
        return [];
      }
      const versions = (await response.json()) as DocumentVersionSummaryDto[];
      set((state) => ({
        versionHistory: { ...state.versionHistory, [documentId]: versions },
      }));
      return versions;
    } catch {
      return [];
    }
  },

  /**
   * Toggle between showing only current versions vs. all versions.
   */
  setShowAllVersions: (show) => {
    set({ showAllVersions: show });
  },

  /**
   * Add a document to the triage batch. Debounces 5s after last upload.
   * When the timer fires, triggers upload triage via useTriageStore.
   */
  addToTriageBatch: (documentId, workspaceId, token) => {
    set((state) => ({
      triagePending: [...(state.triagePending || []), documentId],
      // Optimistisch auf AwaitingConfirmation setzen — zeigt sofort visuellen Indikator
      // während der Debounce-Phase und der LLM-Triage-Verarbeitung
      documents: state.documents.map((doc) =>
        doc.id === documentId
          ? { ...doc, status: 'AwaitingConfirmation' as import('@/types/document').DocumentStatus }
          : doc,
      ),
    }));

    // (Re)start debounce timer
    if (_triageDebounceTimer) clearTimeout(_triageDebounceTimer);
    _triageDebounceTimer = setTimeout(() => {
      _triageDebounceTimer = null;
      const pending = get().triagePending;
      if (pending.length === 0) return;
      set({ triagePending: [] });
      useTriageStore.getState().triggerUploadTriage(workspaceId, pending, token);
    }, TRIAGE_DEBOUNCE_MS);
  },

  /**
   * Clear processing details for a document (after processing completes).
   */
  clearProcessingDetails: (documentId) => {
    set((state) => {
      const { [documentId]: _, ...rest } = state.processingDetails;
      return { processingDetails: rest };
    });
  },

  /**
   * Fetch persisted processing logs from the server.
   * Called on mount so every browser/tab sees the full log history.
   */
  fetchProcessingLogs: async (documentId, token) => {
    try {
      // Skip fetch if we already have logs cached for this document
      const cached = get().processingDetails[documentId];
      if (cached?.logs?.length) return;

      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(
        `/api/dotnet/documents/${documentId}/logs`,
        { headers }
      );
      if (!response.ok) return;

      const serverLogs = (await response.json()) as DocumentLogEntry[];
      if (!serverLogs.length) return;

      // Extract latest stage from server logs (last entry that has a stage)
      const lastStageLog = [...serverLogs].reverse().find((l) => l.stage);

      set((state) => {
        const existing = state.processingDetails[documentId];
        // Merge: server logs as base, keep any client-only logs that came via SignalR after fetch
        const existingTimestamps = new Set(
          (existing?.logs || []).map((l) => `${l.timestamp}|${l.message}`)
        );
        const merged = [
          ...serverLogs.filter((l) => !existingTimestamps.has(`${l.timestamp}|${l.message}`)),
          ...(existing?.logs || []),
        ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        return {
          processingDetails: {
            ...state.processingDetails,
            [documentId]: {
              ...(existing || { lastUpdate: '' }),
              // Populate stage from server logs if not already set by live SignalR events
              stage: existing?.stage || lastStageLog?.stage || undefined,
              logs: merged,
              lastUpdate: existing?.lastUpdate || new Date().toISOString(),
            },
          },
        };
      });
    } catch {
      // Silently fail — logs are non-critical
    }
  },
}),
    {
      name: 'sopho-document-processing',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
      // @ts-expect-error - Zustand persist middleware typing expects full state, but partialize returns partial
      partialize: (state) => ({
        // Only persist processing logs — not documents, upload state, etc.
        processingDetails: state.processingDetails,
      }),
    }
  )
);
